// =========================================================================
// HelpyDoc · Núcleo de CONTENIDO REMOTO (Sistema de Actualización Remota)
// =========================================================================
// Carga genérica de "manifiestos" de contenido editables desde fuera de la app
// (fuente en Drive → publicada como JSON por n8n → leída aquí en runtime). El
// mismo mecanismo sirve para: recomendaciones (T3), apoyo emocional (T4), platos
// recomendados, actividad física y biblioteca (ebook/vídeos).
//
// Principios:
//  • SEGURIDAD: cada consumidor pasa un `fallback` embebido (contenido aprobado
//    en el bundle). Si el remoto falla, no hay red, o el esquema no valida →
//    se usa el fallback. La app NUNCA se queda sin contenido ni se rompe.
//  • INMEDIATEZ: fetch con `cache:'no-cache'` + caché en memoria de TTL corto;
//    al recargar la app se ve el contenido nuevo. No requiere redeploy.
//  • ACCESO GATEADO POR AUTENTICACIÓN: los manifiestos son contenido clínico/
//    editorial global (no datos de usuario), pero su lectura se restringe a
//    usuarios autenticados de la web/app. En producción se leen desde Firebase
//    Storage (`content/*.json`) con reglas `allow read: if request.auth != null`;
//    el SDK adjunta el token del usuario, así que un desconocido con la URL no
//    accede. n8n publica esos objetos desde Drive (prefijo `content/`, con una
//    condición IAM que impide al SA tocar las fotos de pacientes).
//
// Dos fuentes (`VITE_CONTENT_SOURCE`):
//  • 'storage' → Firebase Storage SDK (getBytes), lectura autenticada. PRODUCCIÓN.
//  • 'hosting' / sin configurar → fetch `${VITE_CONTENT_BASE_URL||'/content'}/…`
//    (público, servido por Hosting). Útil en dev/mock donde no hay auth real.
// Ante cualquier fallo (no autenticado, red, esquema) → fallback embebido.
// =========================================================================

const RAW_BASE = (import.meta.env.VITE_CONTENT_BASE_URL as string | undefined) ?? '/content';
const CONTENT_BASE = RAW_BASE.replace(/\/+$/, '');

/** Fuente del contenido: 'storage' (Firebase, autenticado) o 'hosting' (fetch público). */
const CONTENT_SOURCE = (import.meta.env.VITE_CONTENT_SOURCE as string | undefined) ?? 'hosting';
/** Prefijo (carpeta) del contenido dentro del bucket de Firebase Storage. */
const CONTENT_STORAGE_PREFIX =
  ((import.meta.env.VITE_CONTENT_STORAGE_PREFIX as string | undefined) ?? 'content')
    .replace(/^\/+|\/+$/g, '');

/** Lectura pública vía Hosting (dev/mock): `${base}/${key}.json`. */
async function _fetchViaHosting(key: string): Promise<unknown> {
  const res = await fetch(`${CONTENT_BASE}/${key}.json`, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * Lectura autenticada vía Firebase Storage: el SDK adjunta el token del usuario y
 * las Storage Rules exigen `request.auth != null`. Espera a que Auth esté hidratada
 * para que la petición lleve token (si no, las reglas la rechazan → fallback).
 */
async function _fetchViaStorage(key: string): Promise<unknown> {
  // Imports dinámicos: mantienen Firebase FUERA del grafo estático de este módulo
  // (lo importan recomendaciones/apoyo/etc.), para no arrastrarlo a los tests ni
  // al bundle de quien no use Storage.
  const { isFirebaseEnabled, waitForFirebaseReady } = await import('@/services/firebase');
  if (!isFirebaseEnabled) throw new Error('Firebase no configurado');
  await waitForFirebaseReady();
  const { getStorage, ref, getBytes } = await import('firebase/storage');
  const buf = await getBytes(ref(getStorage(), `${CONTENT_STORAGE_PREFIX}/${key}.json`));
  return JSON.parse(new TextDecoder().decode(buf)) as unknown;
}

/**
 * Tope propio para la lectura del contenido remoto.
 *
 * POR QUÉ NO BASTA CON EL `try/catch` DE ABAJO. El fallback embebido solo entra
 * si la promesa RECHAZA. Si en vez de fallar simplemente TARDA, el `await` no se
 * asienta, el `catch` no llega a ejecutarse nunca y la pantalla que espera este
 * contenido se queda girando indefinidamente. El SDK de Storage reintenta hasta
 * 2 minutos por su cuenta, así que "lento" es un modo de fallo real, no teórico:
 * pasó el 2026-08-01 al mover el bucket a Madrid sin configuración CORS — el
 * servidor estaba sano, las reglas bien y las rutas bien, y aun así la pantalla
 * de inicio no pintaba.
 *
 * 6 s: holgado para una conexión móvil lenta y muy por debajo del punto en que
 * un usuario da la app por colgada. Al vencer se usa el contenido embebido, que
 * es válido y está aprobado — degradar es correcto aquí, esperar no.
 */
const LIMITE_MS = 6000;

function _conLimite<T>(p: Promise<T>, key: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`tiempo agotado leyendo ${key}.json`)), LIMITE_MS),
    ),
  ]);
}

/** TTL de la caché en memoria (ms). Corto para que "editar → recargar" se vea ya. */
const TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  at: number;
  data: unknown;
}
const _cache = new Map<string, CacheEntry>();

/** Limpia la caché (útil en tests o para forzar recarga). */
export function clearRemoteContentCache(): void {
  _cache.clear();
}

/**
 * Carga un manifiesto remoto `${base}/${key}.json`, lo valida y lo devuelve.
 * Ante cualquier fallo (red, HTTP, JSON, esquema) devuelve `fallback`.
 *
 * @param key      nombre del manifiesto (sin extensión), p.ej. 'recomendaciones'.
 * @param fallback contenido embebido aprobado (se usa si el remoto no sirve).
 * @param validate predicado de tipo: valida la forma del JSON remoto.
 */
export async function loadRemoteContent<T>(
  key: string,
  fallback: T,
  validate: (raw: unknown) => raw is T,
): Promise<T> {
  const cached = _cache.get(key);
  if (cached && Date.now() - cached.at < TTL_MS) {
    return cached.data as T;
  }
  try {
    const raw: unknown = await _conLimite(
      CONTENT_SOURCE === 'storage' ? _fetchViaStorage(key) : _fetchViaHosting(key),
      key,
    );
    if (!validate(raw)) throw new Error(`esquema inválido en ${key}.json`);
    _cache.set(key, { at: Date.now(), data: raw });
    return raw;
  } catch {
    // Silencioso por diseño: el fallback embebido es contenido válido y aprobado.
    return fallback;
  }
}

/**
 * Ruta del manifiesto dentro del bucket. Se exporta para que el panel de
 * administración ESCRIBA exactamente donde este módulo LEE: si cada lado
 * compusiera la ruta por su cuenta, cambiar el prefijo dejaría al panel
 * guardando donde la app no mira, y el fallo sería mudo (el fallback embebido
 * taparía el hueco y todo "parecería" bien).
 */
export function rutaContenido(key: string): string {
  return `${CONTENT_STORAGE_PREFIX}/${key}.json`;
}

/** Utilidad de validación: objeto plano no nulo. */
export function isObject(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}
